import type { StorefrontPlan, SubscriptionOffer } from "./types";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
}

function url(path: string): string {
  return `${apiBase()}${path}`;
}

async function read<T>(response: Response): Promise<T> {
  const text = await response.text();
  let body: (T & { detail?: unknown; error?: string }) | null = null;
  try {
    body = text ? (JSON.parse(text) as T & { detail?: unknown; error?: string }) : null;
  } catch {
    throw new Error(text.trim().slice(0, 200) || `Request failed (${response.status})`);
  }
  if (!response.ok) {
    const detail = body?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : typeof detail === "object" && detail && "error" in (detail as object)
          ? String((detail as { error?: string }).error)
          : body?.error || `Request failed (${response.status})`;
    throw new Error(msg);
  }
  if (!body) {
    throw new Error(`Request failed (${response.status})`);
  }
  return body;
}

export async function fetchOffer(id: string): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}`));
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function activateOffer(
  id: string,
  payer: string,
  approveTx?: string
): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/activate`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payer, approve_tx: approveTx }),
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function fetchStorefront(slug: string): Promise<StorefrontPlan> {
  const res = await fetch(url(`/v1/storefront/${encodeURIComponent(slug)}`));
  const body = await read<{ success: boolean; data: StorefrontPlan }>(res);
  return body.data;
}

export async function fetchStorefrontSubscription(
  slug: string,
  reference: string
): Promise<SubscriptionOffer | null> {
  const ref = reference.trim();
  if (!ref) return null;
  const params = new URLSearchParams({ reference: ref });
  const res = await fetch(url(`/v1/storefront/${encodeURIComponent(slug)}/subscription?${params}`));
  const body = await read<{ success: boolean; data: SubscriptionOffer | null }>(res);
  return body.data || null;
}

export async function retryOffer(id: string): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/retry`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function cancelOffer(id: string): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/subscriptions/${encodeURIComponent(id)}/cancel`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function subscribeStorefront(
  slug: string,
  assetId: string,
  contact: { name: string; email: string; reference?: string | null; planId?: string | null }
): Promise<SubscriptionOffer> {
  const res = await fetch(url(`/v1/storefront/${encodeURIComponent(slug)}/subscribe`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      asset_id: assetId,
      customer_name: contact.name,
      customer_email: contact.email,
      reference: contact.reference || undefined,
      plan_id: contact.planId || undefined,
    }),
  });
  const body = await read<{ success: boolean; data: SubscriptionOffer }>(res);
  return body.data;
}

export async function waitForSubscriptionStatus(
  id: string,
  done: (offer: SubscriptionOffer) => boolean,
  opts: { intervalMs?: number; timeoutMs?: number } = {}
): Promise<SubscriptionOffer> {
  const intervalMs = opts.intervalMs ?? 2000;
  const timeoutMs = opts.timeoutMs ?? 180_000;
  const started = Date.now();
  let offer = await fetchOffer(id);
  while (!done(offer)) {
    if (Date.now() - started > timeoutMs) {
      throw new Error("Timed out waiting for payment confirmation");
    }
    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
    offer = await fetchOffer(id);
  }
  return offer;
}
